import { useState, useEffect } from 'react';
import { Link, useLocation } from '@tanstack/react-router';

const API = import.meta.env.VITE_API_URL || '/api';

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [tenantName, setTenantName] = useState<string | null>(null);

  const token = localStorage.getItem('accessToken');
  const tenantSlug = localStorage.getItem('tenantSlug') || 'test';

  useEffect(() => {
    fetch(`${API}/tenant-info/${tenantSlug}`)
      .then(res => res.ok ? res.json() : null)
      .then(d => { if (d?.data?.name) setTenantName(d.data.name); })
      .catch(() => {});
  }, [tenantSlug]);

  useEffect(() => {
    if (token) {
      fetch(`${API}/admin/tenant/logo`, { headers: { 'Authorization': `Bearer ${token}`, 'x-tenant-slug': tenantSlug } })
        .then(res => { if (res.ok) setLogoUrl(`${API}/tenant-logo/${tenantSlug}?t=${Date.now()}`); })
        .catch(() => {});
    }
  }, [token, tenantSlug]);

  useEffect(() => {
    // Check localStorage for dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode !== null) {
      setDarkMode(savedDarkMode === 'true');
    } else {
      // If no preference, check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(prefersDark);
    }
  }, []);

  useEffect(() => {
    // Apply dark mode class to document root
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save preference to localStorage
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Collapsible sidebar (desktop): collapsed shows only icons, freeing screen space.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebarCollapsed', String(collapsed)); } catch {}
  }, [collapsed]);

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userRole = user?.role || 'cashier';

  // Role-based nav items
  const allNavItems = [
    { path: '/', label: 'Dashboard', icon: '🏠', roles: ['admin', 'manager', 'cashier'] },
    { path: '/products', label: 'Productos', icon: '📦', roles: ['admin', 'manager'] },
    { path: '/pos', label: 'Punto de Venta', icon: '🛒', roles: ['admin', 'manager', 'cashier'] },
    { path: '/customers', label: 'Clientes', icon: '👥', roles: ['admin', 'manager'] },
    { path: '/suppliers', label: 'Proveedores', icon: '🚚', roles: ['admin', 'manager'] },
    { path: '/purchase-orders', label: 'Órdenes de Compra', icon: '📋', roles: ['admin', 'manager'] },
    { path: '/invoices', label: 'Facturas', icon: '🧾', roles: ['admin', 'manager'] },
    { path: '/reports', label: 'Reportes', icon: '📊', roles: ['admin', 'manager'] },
    { path: '/cash', label: 'Caja', icon: '💵', roles: ['admin', 'manager', 'cashier'] },
    { path: '/documentation', label: 'Ayuda', icon: '📖', roles: ['admin', 'manager', 'cashier'] },
    { path: '/admin', label: 'Administración', icon: '⚙️', roles: ['admin', 'manager'] },
    { path: '/backups', label: 'Backups', icon: '💾', roles: ['admin'] },
  ];
  const navItems = allNavItems.filter(item => item.roles.includes(userRole));

  function handleLogout() {
    // Revoke the refresh token server-side (fire and forget)
    const rt = localStorage.getItem('refreshToken');
    if (rt) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-tenant-slug': localStorage.getItem('tenantSlug') || '' },
        body: JSON.stringify({ refreshToken: rt }),
      }).catch(() => {});
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantSlug');
    window.location.href = '/login';
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transform transition-all duration-200 lg:translate-x-0 flex flex-col ${collapsed ? 'lg:w-16' : 'lg:w-64'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className={`shrink-0 border-b border-gray-700 flex items-center gap-2 ${collapsed ? 'lg:px-2 lg:py-4 lg:justify-center' : 'p-6 justify-between'}`}>
          <h1 className={`font-bold flex items-center gap-2 min-w-0 ${collapsed ? '' : 'text-xl'}`}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px' }} />
            ) : (
              <span className="text-xl">🏪</span>
            )}
            {!collapsed && <span className="block min-w-0 leading-snug whitespace-normal">{tenantName || 'Mi Comercio'}</span>}
          </h1>
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expandir menú' : 'Minimizar menú'}
            aria-label={collapsed ? 'Expandir menú' : 'Minimizar menú'}
            className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white cursor-pointer min-h-[32px] shrink-0"
          >
            {collapsed ? '▶' : '◀'}
          </button>
        </div>
        {!collapsed && <p className="text-xs text-gray-400 px-6 pb-2 hidden lg:block">Retail SaaS</p>}

        <nav className={`flex-1 overflow-y-auto ${collapsed ? 'lg:p-2 lg:space-y-1' : 'p-4 space-y-1'}`}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={`flex items-center gap-3 rounded-lg text-sm transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'} ${collapsed ? 'lg:justify-center lg:px-0 lg:py-3' : 'px-4 py-2'}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="text-lg shrink-0">{item.icon}</span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className={`shrink-0 w-full border-t border-gray-700 ${collapsed ? 'lg:p-2' : 'p-4'}`}>
          <div className={`flex items-center gap-3 mb-3 ${collapsed ? 'lg:justify-center' : ''}`}>
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
            )}
          </div>

          {/* Dark Mode Toggle */}
          <div className={`flex items-center mb-4 ${collapsed ? 'lg:justify-center' : 'gap-3'}`}>
            <span className={`text-xs text-gray-400 ${collapsed ? 'hidden' : ''}`}>Modo oscuro</span>
            <button
              onClick={toggleDarkMode}
              title="Modo oscuro"
              className={`w-11 h-6 bg-gray-600 rounded-full cursor-pointer flex items-center transition-colors ${darkMode ? 'translate-x-5' : 'translate-x-0'}`}
            >
              <span className="block h-4 w-4 bg-white rounded-full shadow"></span>
            </button>
          </div>

          <button
            onClick={handleLogout}
            title={collapsed ? 'Cerrar sesión' : undefined}
            className={`w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg transition-colors ${collapsed ? 'lg:px-0 lg:text-center' : ''}`}
          >
            {collapsed ? '🚪' : '🚪 Cerrar sesión'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-2xl">
            ☰
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2 dark:text-gray-100">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '3px' }} />
            ) : (
              <span>🏪</span>
            )}
            {tenantName || 'Mi Comercio'}
          </h1>
          <div className="w-8" />
        </header>


        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
}